<template>
  <div
    class="app-shell"
    :class="{
      'route-wide': isWide,
      'is-mobile': isMobile,
      'nav-open': mobileNavOpen,
      'has-bottom-nav': isMobile,
    }"
  >
    <div
      v-if="isMobile && mobileNavOpen"
      class="nav-mask"
      @click="mobileNavOpen = false"
    />

    <aside
      class="sidebar"
      :class="{
        collapsed: !isMobile && sidebarCollapsed,
        'mobile-drawer': isMobile,
        open: mobileNavOpen,
      }"
    >
      <div class="brand">
        <div class="brand-mark">M<span>✦</span></div>
        <div v-show="isMobile || !sidebarCollapsed" class="brand-text">
          <strong>MediaPilot</strong>
          <small>AI 内容运营中枢</small>
        </div>
        <button
          v-if="isMobile"
          type="button"
          class="drawer-close"
          aria-label="关闭菜单"
          @click="mobileNavOpen = false"
        >
          ×
        </button>
      </div>

      <nav class="nav">
        <template v-for="group in navGroups" :key="group.label || 'main'">
          <div v-if="group.label" class="nav-label" :class="{ lower: group.lower }">{{ group.label }}</div>
          <router-link
            v-for="item in group.items"
            :key="item.key"
            :to="item.to"
            class="nav-item"
            :class="{ active: isActive(item) }"
            :title="!isMobile && sidebarCollapsed ? item.label : undefined"
            @click="onNavClick"
          >
            <el-icon class="nav-ico"><component :is="item.icon" /></el-icon>
            <span v-show="isMobile || !sidebarCollapsed" class="nav-text">{{ item.label }}</span>
            <em
              v-if="navBadge(item) && (isMobile || !sidebarCollapsed)"
              :class="item.badgeClass || (item.badgeKey === 'cookie' ? 'warn' : 'new')"
            >{{ navBadge(item) }}</em>
          </router-link>
        </template>
      </nav>

      <div class="sidebar-footer">
        <button
          v-show="isMobile || !sidebarCollapsed"
          type="button"
          class="plan"
          @click="agentVisible = true; mobileNavOpen = false"
        >
          <span>✦</span>
          <div>
            <b>让 Agent 处理当前页</b>
            <small>读取页面内容 · 生成 · 批量发布</small>
          </div>
        </button>
        <button
          v-if="!isMobile"
          type="button"
          class="collapse"
          @click="sidebarCollapsed = !sidebarCollapsed"
        >
          {{ sidebarCollapsed ? '›' : '‹　收起侧栏' }}
        </button>
      </div>
    </aside>

    <div class="main-column">
      <header class="topbar">
        <button
          v-if="isMobile"
          type="button"
          class="menu-btn"
          aria-label="打开菜单"
          @click="mobileNavOpen = true"
        >
          <span /><span /><span />
        </button>

        <div class="crumb">
          <span class="live-dot" />
          <template v-if="currentSection && currentSection.key !== 'home'">
            <router-link :to="currentSection.to" class="crumb-section">{{ currentSection.label }}</router-link>
            <i v-if="pageTitle && pageTitle !== currentSection.label">/</i>
          </template>
          <span v-if="!currentSection || currentSection.key === 'home' || pageTitle !== currentSection.label">
            {{ pageTitle }}
          </span>
        </div>

        <div v-if="!isMobile" class="global-search">
          <span>⌕</span>
          <input
            v-model="searchQ"
            placeholder="搜索内容库，或账号关键词…"
            @keydown.enter="onSearch"
          />
          <kbd>⌘ K</kbd>
        </div>

        <StatusChips
          :sms="smsNotification"
          :cookie-count="cookieNeedCount"
          :cookie-visible="cookieAlertVisible"
          :cookie-preview="cookieAlertPreview"
          @open-sms="goOpenSms"
          @dismiss-sms="dismissSmsNotification"
          @fix-login="goFixLogin"
          @dismiss-cookie="dismissCookieAlert"
        />

        <div class="top-actions">
          <el-button
            :type="agentVisible ? 'primary' : 'default'"
            :icon="ChatDotRound"
            round
            @click="agentVisible = !agentVisible"
          >
            <span v-if="!isMobile">Agent</span>
          </el-button>
        </div>
      </header>

      <div class="content-row">
        <main class="main" :class="{ 'main--dense': isWide }">
          <router-view />
        </main>
        <transition name="slide">
          <aside v-if="agentVisible" class="agent-sidebar">
            <div v-if="isMobile" class="agent-mobile-bar">
              <strong>Agent</strong>
              <button type="button" @click="agentVisible = false">关闭</button>
            </div>
            <AgentSidebar @close="agentVisible = false" @view-article="onAgentViewArticle" />
          </aside>
        </transition>
        <div
          v-if="isMobile && agentVisible"
          class="agent-mask"
          @click="agentVisible = false"
        />
      </div>

      <nav v-if="isMobile" class="bottom-nav" aria-label="主导航">
        <router-link
          v-for="item in bottomNav"
          :key="item.key"
          :to="item.to"
          class="bn-item"
          :class="{ active: isActive(item) }"
        >
          <el-icon><component :is="item.icon" /></el-icon>
          <span>{{ item.label }}</span>
          <em v-if="navBadge(item)" class="bn-badge">{{ navBadge(item) }}</em>
        </router-link>
        <button type="button" class="bn-item" @click="mobileNavOpen = true">
          <el-icon><Menu /></el-icon>
          <span>更多</span>
        </button>
      </nav>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ChatDotRound, Menu } from '@element-plus/icons-vue'
import { ElMessage, ElNotification } from 'element-plus'
import AgentSidebar from '../components/AgentSidebar.vue'
import StatusChips from '../components/shell/StatusChips.vue'
import { publishV2Api, smsApi } from '../api'
import { useAgentStore } from '../stores/agentStore'
import {
  BOTTOM_NAV_KEYS,
  NAV_GROUPS,
  isItemActive,
  sectionByKey,
  sectionForPath,
} from '../config/navigation'

const NAV = NAV_GROUPS.map((g) => ({ ...g, items: g.keys.map(sectionByKey).filter(Boolean) }))
const BOTTOM_NAV = BOTTOM_NAV_KEYS.map(sectionByKey).filter(Boolean)

const COOKIE_DISMISS_KEY = 'mp_cookie_alert_dismissed_at'
const PLATFORM_LABEL = {
  xiaohongshu: '小红书', douyin: '抖音', zhihu: '知乎', bilibili: 'B站',
  toutiao: '头条', baijiahao: '百家号', kuaishou: '快手', weibo: '微博',
}
const MOBILE_MQ = '(max-width: 768px)'

export default {
  name: 'Layout',
  components: { AgentSidebar, StatusChips, Menu },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const agentVisible = useAgentStore().panelOpen
    const sidebarCollapsed = ref(false)
    const mobileNavOpen = ref(false)
    const isMobile = ref(false)
    const searchQ = ref('')
    const cookieNeedCount = ref(0)
    const cookieNeedList = ref([])
    const cookieAlertVisible = ref(false)
    const smsNotification = ref(null)
    let smsTimer = null
    let smsLastId = 0
    const smsSeenIds = new Set(
      JSON.parse(localStorage.getItem('mediapilot_sms_seen_ids') || '[]'),
    )
    const smsSeenKeys = new Set(
      JSON.parse(localStorage.getItem('mediapilot_sms_seen_keys') || '[]'),
    )
    let cookieTimer = null
    let notifiedOnce = false
    let mediaQuery = null

    const currentSection = computed(() => sectionForPath(String(route.path || '/')))
    const isWide = computed(() => ['data', 'publish', 'discover'].includes(currentSection.value?.key))
    const pageTitle = computed(() => route.name || currentSection.value?.label || '工作台')

    const syncMobile = () => {
      try {
        isMobile.value = !!mediaQuery?.matches
        if (isMobile.value) {
          mobileNavOpen.value = false
          sidebarCollapsed.value = false
        }
      } catch { /* ignore */ }
    }

    const maybeCollapseForWide = () => {
      if (isMobile.value || !isWide.value) return
      try {
        if (typeof window !== 'undefined' && window.innerWidth < 1280) {
          sidebarCollapsed.value = true
        }
      } catch { /* ignore */ }
    }

    const cookieAlertPreview = computed(() => {
      const list = cookieNeedList.value || []
      if (!list.length) return '请到发布中心重新登录'
      const names = list.slice(0, 3).map((a) => {
        const p = PLATFORM_LABEL[a.platform] || a.platform
        return `${p}·${a.display_name || a.account_id}`
      })
      const more = list.length > 3 ? ` 等 ${list.length} 个` : ''
      return `${names.join('、')}${more}`
    })

    const isDismissedRecently = () => {
      try {
        const raw = sessionStorage.getItem(COOKIE_DISMISS_KEY)
        if (!raw) return false
        const ts = Number(raw)
        return Number.isFinite(ts) && Date.now() - ts < 30 * 60 * 1000
      } catch {
        return false
      }
    }

    const refreshCookieHealth = async ({ notify = false } = {}) => {
      try {
        const data = await publishV2Api.getCookieHealth()
        const need = data?.needs_relogin || []
        cookieNeedCount.value = Number(data?.needs_relogin_count || need.length || 0)
        cookieNeedList.value = need
        cookieAlertVisible.value = cookieNeedCount.value > 0 && !isDismissedRecently()
        if (notify && cookieNeedCount.value > 0 && !notifiedOnce) {
          notifiedOnce = true
          ElNotification({
            title: '账号登录失效',
            message: `检测到 ${cookieNeedCount.value} 个账号 Cookie 过期或未登录，请尽快重新登录`,
            type: 'warning',
            duration: 8000,
            onClick: () => goFixLogin(),
          })
        }
      } catch {
        /* ignore */
      }
    }

    const dismissCookieAlert = () => {
      cookieAlertVisible.value = false
      try {
        sessionStorage.setItem(COOKIE_DISMISS_KEY, String(Date.now()))
      } catch { /* ignore */ }
    }

    const goFixLogin = () => {
      dismissCookieAlert()
      router.push({ path: '/publish', query: { health: 'bad' } })
    }

    const goOpenSms = () => {
      router.push({ path: '/publish', query: { tab: 'sms' } })
    }

    const dismissSmsNotification = () => {
      smsNotification.value = null
    }

    const pollSmsNotifications = async () => {
      try {
        const data = await smsApi.notifications(smsLastId)
        const items = (data?.items || []).filter((item) => {
          const id = Number(item.id) || 0
          if (!id || smsSeenIds.has(id)) return false
          const normalizedSender = String(item.sender_phone || '').replace(/^\+/, '')
          const normalizedCode = String(item.verification_code || '')
          const contentKey = [
            normalizedSender,
            String(item.local_identity || item.device_phone || ''),
            normalizedCode,
            String(item.process_result || ''),
            String(item.created_at || '').slice(0, 16),
          ].join('|')
          if (smsSeenKeys.has(contentKey)) {
            smsSeenIds.add(id)
            return false
          }
          smsSeenIds.add(id)
          smsSeenKeys.add(contentKey)
          localStorage.setItem(
            'mediapilot_sms_seen_ids',
            JSON.stringify([...smsSeenIds].slice(-200)),
          )
          localStorage.setItem(
            'mediapilot_sms_seen_keys',
            JSON.stringify([...smsSeenKeys].slice(-200)),
          )
          return true
        })
        if (items.length) {
          smsLastId = Math.max(...items.map((item) => Number(item.id) || 0), smsLastId)
          smsNotification.value = items[items.length - 1]
        }
      } catch {
        /* 短信通知失败不影响其他页面 */
      }
    }

    const navBadge = (item) => {
      if (item.badgeKey === 'cookie' && cookieNeedCount.value > 0) {
        return String(cookieNeedCount.value)
      }
      return item.badge || ''
    }

    const isActive = (item) => isItemActive(item, route.path)

    const onNavClick = () => {
      if (isMobile.value) mobileNavOpen.value = false
    }

    const onSearch = () => {
      const q = searchQ.value.trim()
      if (!q) {
        router.push('/content/library')
        return
      }
      const looksLikeAccount = /账号|uid|mp_|@/i.test(q) || /^[a-z0-9_-]{6,}$/i.test(q)
      if (looksLikeAccount) {
        ElMessage.info(`正在发布中心查找账号「${q}」…`)
        router.push({ path: '/publish', query: { q } })
        return
      }
      ElMessage.info(`正在内容库搜索「${q}」…`)
      router.push({ path: '/content/library', query: { q } })
    }

    onMounted(() => {
      try {
        mediaQuery = window.matchMedia(MOBILE_MQ)
        syncMobile()
        mediaQuery.addEventListener?.('change', syncMobile)
        mediaQuery.addListener?.(syncMobile)
      } catch { /* ignore */ }
      maybeCollapseForWide()
      refreshCookieHealth({ notify: true })
      publishV2Api.checkCookieHealth({ live: true }).catch(() => {})
      cookieTimer = setInterval(() => refreshCookieHealth(), 120000)
      setTimeout(() => refreshCookieHealth({ notify: false }), 25000)
      pollSmsNotifications()
      smsTimer = setInterval(pollSmsNotifications, 5000)
    })

    onUnmounted(() => {
      if (cookieTimer) clearInterval(cookieTimer)
      if (smsTimer) clearInterval(smsTimer)
      try {
        mediaQuery?.removeEventListener?.('change', syncMobile)
        mediaQuery?.removeListener?.(syncMobile)
      } catch { /* ignore */ }
    })

    watch(
      () => route.path,
      () => {
        mobileNavOpen.value = false
        if (isMobile.value) agentVisible.value = false
        maybeCollapseForWide()
      },
    )

    watch(isMobile, (v) => {
      if (v) document.documentElement.classList.add('mp-h5')
      else document.documentElement.classList.remove('mp-h5')
    }, { immediate: true })

    const onAgentViewArticle = (article) => {
      if (!article?.id) return
      router.push({ path: '/content/library', query: { open: String(article.id) } })
    }

    return {
      onAgentViewArticle,
      currentSection,
      isWide,
      isMobile,
      mobileNavOpen,
      navGroups: NAV,
      bottomNav: BOTTOM_NAV,
      agentVisible,
      sidebarCollapsed,
      searchQ,
      pageTitle,
      ChatDotRound,
      Menu,
      cookieNeedCount,
      cookieAlertVisible,
      cookieAlertPreview,
      isActive,
      onSearch,
      onNavClick,
      navBadge,
      dismissCookieAlert,
      goFixLogin,
      goOpenSms,
      smsNotification,
      dismissSmsNotification,
    }
  },
}
</script>

<style scoped>
.app-shell {
  display: flex;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  background: var(--mp-bg);
  font-size: 13px;
}

.sidebar {
  width: var(--mp-sidebar-width);
  background: var(--mp-surface);
  border-right: 1px solid var(--mp-border);
  padding: 22px 14px 16px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  transition: width 0.2s ease, transform 0.25s ease;
  z-index: 40;
}

.sidebar.collapsed {
  width: 72px;
  padding-left: 10px;
  padding-right: 10px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 0 8px 22px;
}

.brand-mark {
  width: 33px;
  height: 33px;
  border-radius: 10px;
  background: var(--mp-text);
  color: #fff;
  display: grid;
  place-items: center;
  font-size: 18px;
  font-weight: 800;
  position: relative;
  flex-shrink: 0;
}

.brand-mark span {
  position: absolute;
  top: 2px;
  right: 3px;
  color: #aabfff;
  font-size: 9px;
}

.brand-text strong {
  display: block;
  font-size: 15px;
  letter-spacing: -0.4px;
  color: var(--mp-text);
  font-weight: 800;
}

.brand-text small {
  display: block;
  color: var(--mp-text-muted);
  font-size: 10px;
  margin-top: 3px;
}

.drawer-close {
  margin-left: auto;
  border: 0;
  background: #f3f5f8;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  font-size: 20px;
  line-height: 1;
  color: var(--mp-text-muted);
  cursor: pointer;
}

.nav {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.nav-label {
  margin-top: 18px;
  padding: 0 11px 7px;
  color: #a3acbc;
  font-size: 10px;
  letter-spacing: 1.2px;
  font-weight: 700;
}

.nav-label.lower {
  margin-top: 22px;
}

.sidebar.collapsed .nav-label {
  display: none;
}

.nav-item {
  height: 40px;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 0 11px;
  color: var(--mp-text-secondary);
  text-decoration: none;
  border-radius: 9px;
  margin: 3px 0;
  font-size: 12px;
  font-weight: 600;
}

.nav-item:hover {
  background: var(--mp-surface-muted);
}

.nav-item.active {
  color: var(--mp-accent);
  background: var(--mp-accent-soft);
}

.nav-ico {
  width: 17px;
  font-size: 16px;
  color: #9ca7b8;
  flex-shrink: 0;
}

.nav-item.active .nav-ico {
  color: var(--mp-accent);
}

.nav-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nav-item em {
  font-style: normal;
  font-size: 10px;
  background: #f0f2f6;
  color: #929db1;
  border-radius: 10px;
  padding: 2px 6px;
  margin-left: auto;
}

.nav-item em.new {
  background: var(--mp-success-soft);
  color: var(--mp-success);
  font-size: 9px;
}

.nav-item em.warn {
  background: #fff1e8;
  color: #d9480f;
  font-size: 10px;
  font-weight: 600;
}

.sidebar-footer {
  margin-top: auto;
  padding-top: 8px;
}

.plan {
  display: flex;
  gap: 10px;
  align-items: center;
  width: 100%;
  border: 1px solid transparent;
  border-radius: 12px;
  padding: 12px;
  background: linear-gradient(135deg, #f0f5ff, #f8f4ff);
  margin-bottom: 10px;
  font: inherit;
  color: var(--mp-text);
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease;
}

.plan:hover {
  border-color: var(--mp-accent-glow);
}

.plan > span {
  color: var(--mp-orange);
  font-size: 18px;
}

.plan b {
  display: block;
  font-size: 11px;
}

.plan small {
  display: block;
  color: var(--mp-text-muted);
  font-size: 10px;
  margin-top: 2px;
}

.collapse {
  background: transparent;
  border: 0;
  color: var(--mp-text-muted);
  font-size: 11px;
  padding: 8px 10px;
  cursor: pointer;
  font-family: inherit;
  width: 100%;
  text-align: left;
}

.main-column {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.topbar {
  height: var(--mp-header-height);
  padding: 0 28px;
  border-bottom: 1px solid var(--mp-border);
  display: flex;
  align-items: center;
  gap: 20px;
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(8px);
  flex-shrink: 0;
  z-index: 2;
}

.menu-btn {
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 9px;
  background: #f3f5f8;
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
}

.menu-btn span {
  display: block;
  width: 16px;
  height: 2px;
  background: var(--mp-text-secondary);
  border-radius: 2px;
}

.crumb {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: var(--mp-text-secondary);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.crumb-section {
  color: var(--mp-text-secondary);
  text-decoration: none;
  font-weight: 650;
}

.crumb-section:hover {
  color: var(--mp-accent);
}

.crumb-mono {
  font-family: var(--mp-font-mono);
  font-size: 10px;
  color: var(--mp-text-muted);
  letter-spacing: 1px;
}

.crumb i {
  font-style: normal;
  color: #d6dbe5;
  margin: 0 8px;
}

.live-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  background: #34be8b;
  border-radius: 50%;
  margin-right: 8px;
  box-shadow: 0 0 0 3px #e0f6ed;
  flex-shrink: 0;
}

.global-search {
  margin-left: auto;
  width: 260px;
  height: 35px;
  border: 1px solid var(--mp-border);
  border-radius: 8px;
  background: #fff;
  display: flex;
  align-items: center;
  padding: 0 9px;
  gap: 8px;
  color: #a2adbe;
  flex-shrink: 0;
}

.global-search input {
  border: 0;
  outline: 0;
  width: 100%;
  font-size: 11px;
  font-family: inherit;
  background: transparent;
  color: var(--mp-text);
}

.global-search kbd {
  white-space: nowrap;
  color: #b0b8c7;
  background: #f4f6f9;
  border-radius: 4px;
  padding: 3px 5px;
  font-size: 9px;
  font-family: var(--mp-font-mono);
}

.top-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  margin-left: auto;
}

.content-row {
  flex: 1;
  min-height: 0;
  display: flex;
  overflow: hidden;
  position: relative;
}

.main {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 32px 40px 56px;
  background: var(--mp-bg);
  -webkit-overflow-scrolling: touch;
}

.main.main--dense {
  padding: 12px 16px 40px;
}

.app-shell.route-wide .main.main--dense {
  padding: 12px 14px 36px;
}

.agent-sidebar {
  width: 420px;
  background: var(--mp-surface);
  border-left: 1px solid var(--mp-border);
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  z-index: 30;
}

.agent-mobile-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid var(--mp-border);
  flex-shrink: 0;
}

.agent-mobile-bar button {
  border: 0;
  background: #f3f5f8;
  border-radius: 8px;
  padding: 6px 12px;
  font: inherit;
  font-size: 12px;
  font-weight: 650;
  color: var(--mp-text-secondary);
  cursor: pointer;
}

.agent-mask,
.nav-mask {
  position: absolute;
  inset: 0;
  background: rgba(23, 32, 51, 0.36);
  z-index: 25;
}

.nav-mask {
  position: fixed;
  z-index: 35;
}

.bottom-nav {
  display: none;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.25s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

@media (max-width: 960px) {
  .global-search {
    display: none;
  }
  .main {
    padding: 24px 18px 40px;
  }
  .main.main--dense {
    padding: 8px 8px 28px;
  }
}

/* —— H5 —— */
@media (max-width: 768px) {
  .menu-btn {
    display: flex;
  }

  .sidebar {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: min(86vw, 300px);
    padding-top: calc(16px + var(--mp-safe-top));
    padding-bottom: calc(16px + var(--mp-safe-bottom));
    transform: translateX(-105%);
    box-shadow: 8px 0 32px rgba(30, 48, 82, 0.18);
    border-right: 0;
  }

  .sidebar.mobile-drawer.open {
    transform: translateX(0);
  }

  .sidebar.collapsed {
    width: min(86vw, 300px);
  }

  .topbar {
    height: 56px;
    padding: 0 12px;
    padding-top: var(--mp-safe-top);
    height: calc(56px + var(--mp-safe-top));
    gap: 10px;
  }

  .crumb {
    display: none;
  }

  .top-actions {
    margin-left: 0;
    gap: 6px;
  }

  .topbar :deep(.status-chips) {
    margin-left: auto;
    min-width: 0;
    overflow: hidden;
  }

  .main {
    padding: 14px 12px calc(24px + var(--mp-bottom-nav-height) + var(--mp-safe-bottom));
  }

  .main.main--dense,
  .app-shell.route-wide .main.main--dense {
    padding: 10px 10px calc(20px + var(--mp-bottom-nav-height) + var(--mp-safe-bottom));
  }

  .agent-sidebar {
    position: absolute;
    inset: 0;
    width: 100%;
    border-left: 0;
    z-index: 40;
  }

  .bottom-nav {
    display: flex;
    align-items: stretch;
    justify-content: space-around;
    flex-shrink: 0;
    min-height: calc(var(--mp-bottom-nav-height) + var(--mp-safe-bottom));
    padding-bottom: var(--mp-safe-bottom);
    background: rgba(255, 255, 255, 0.94);
    backdrop-filter: blur(10px);
    border-top: 1px solid var(--mp-border);
    z-index: 20;
  }

  .bn-item {
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding: 8px 4px;
    border: 0;
    background: transparent;
    color: var(--mp-text-muted);
    text-decoration: none;
    font: inherit;
    font-size: 10px;
    font-weight: 650;
    cursor: pointer;
  }

  .bn-item .el-icon {
    font-size: 20px;
  }

  .bn-item.active {
    color: var(--mp-accent);
  }

  .bn-badge {
    position: absolute;
    top: 4px;
    right: calc(50% - 22px);
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border-radius: 999px;
    background: #d9480f;
    color: #fff;
    font-size: 10px;
    font-style: normal;
    line-height: 16px;
    text-align: center;
  }
}
</style>
